/**
 * bundleStarburstEdges.ts — collapse "many edges share one tx_hash and converge
 * on one node" patterns into a single bundle edge + a cluster placeholder node.
 *
 * Why this exists
 * ---------------
 * Some on-chain transactions touch hundreds of addresses on one side: CoinJoins,
 * mixer outputs, exchange batch sweeps, mining payouts, etc. When such a tx
 * lands in a forensic subpoena (or an on-chain BTC fetch), the parser/fetcher
 * faithfully emits one edge per co-spender. Rendering all of them produces a
 * starburst that visually claims "228 addresses sent money to this wallet" —
 * which is misleading. They were merely co-inputs of the same on-chain tx.
 *
 * This utility runs canvas-side, AFTER the per-source builders (forensic
 * overlay, on-chain expansion, etc.) and BEFORE the elements hit Cytoscape.
 * It detects starbursts using the convention every data path shares
 * (`tx_hash` on the edge + a single `target` or `source` node) and replaces
 * each starburst with one bundle edge + one cluster node.
 *
 * Single point of truth — applies uniformly to forensic, on-chain BTC,
 * future batched-payout sources, etc.
 *
 * Click-to-expand: the cluster node carries the original edges + the
 * source/target node ids it absorbed under `bundled_edges` / `bundled_node_ids`.
 * The Workbench `handleNodeClick` recognises `type==='cluster'` and swaps the
 * cluster + bundle edge for the originals on demand.
 */

export const STARBURST_THRESHOLD = 10;

export interface BundleOptions {
    /** Minimum edge count before bundling kicks in. Default 10. */
    threshold?: number;
}

interface EdgeRow { data: any }
interface NodeRow { data: any }

function isEdge(el: any): el is EdgeRow {
    return !!el?.data?.source && !!el?.data?.target;
}

function isNode(el: any): el is NodeRow {
    return !!el?.data?.id && !el?.data?.source;
}


/**
 * Returns a transformed elements array where starburst edge groups are bundled.
 * Pure function — no side effects, no Cytoscape access.
 *
 * Detection: groups edges by (tx_hash, convergence_node_id, direction) where
 * direction is "in" (many sources → one target) or "out" (one source → many
 * targets). Edges without tx_hash are never bundled.
 *
 * Removed nodes: a source/target node is removed from the canvas only if ALL
 * its edges land in the same bundled group (i.e. the node is exclusively a
 * co-spender of this one tx). Nodes with edges to other things stay visible.
 */
export function bundleStarburstEdges(elements: any[], opts: BundleOptions = {}): any[] {
    const threshold = opts.threshold ?? STARBURST_THRESHOLD;
    if (!Array.isArray(elements) || elements.length === 0) return elements;

    // Index edges by (tx_hash, target, "in") and (tx_hash, source, "out").
    // Skip edges without a tx_hash — bundling is identity-anchored, not topological.
    const inboundGroups = new Map<string, EdgeRow[]>();
    const outboundGroups = new Map<string, EdgeRow[]>();
    const edgeIndexById = new Map<string, EdgeRow>();
    const edges: EdgeRow[] = [];
    const nodes: NodeRow[] = [];

    for (const el of elements) {
        if (isEdge(el)) {
            edges.push(el);
            if (el.data.id) edgeIndexById.set(el.data.id, el);
            const tx = el.data.tx_hash;
            if (!tx) continue;
            const inKey = `${tx}|${el.data.target}|in`;
            const outKey = `${tx}|${el.data.source}|out`;
            (inboundGroups.get(inKey) ?? inboundGroups.set(inKey, []).get(inKey)!).push(el);
            (outboundGroups.get(outKey) ?? outboundGroups.set(outKey, []).get(outKey)!).push(el);
        } else if (isNode(el)) {
            nodes.push(el);
        }
    }

    // Pick groups that exceed the threshold. Prefer the side with MORE edges
    // when both inbound and outbound qualify for the same tx (rare — tx with
    // many inputs AND many outputs, e.g. CoinJoin centred on a hub address).
    const bundles: Array<{
        side: 'in' | 'out';
        convergence: string;
        tx_hash: string;
        edges: EdgeRow[];
    }> = [];
    const edgeIdsClaimed = new Set<string>();

    const candidates: Array<{ side: 'in' | 'out'; key: string; group: EdgeRow[] }> = [];
    for (const [key, group] of inboundGroups) {
        if (group.length > threshold) candidates.push({ side: 'in', key, group });
    }
    for (const [key, group] of outboundGroups) {
        if (group.length > threshold) candidates.push({ side: 'out', key, group });
    }
    // Sort largest-first so a CoinJoin (228 in / 228 out) bundles the bigger
    // side first, then leaves the other side's edges to be considered (some
    // may be claimed already, but the side with more edges wins).
    candidates.sort((a, b) => b.group.length - a.group.length);

    for (const c of candidates) {
        const remaining = c.group.filter(e => !edgeIdsClaimed.has(e.data.id));
        if (remaining.length <= threshold) continue;
        for (const e of remaining) edgeIdsClaimed.add(e.data.id);
        const sample = remaining[0];
        bundles.push({
            side: c.side,
            convergence: c.side === 'in' ? sample.data.target : sample.data.source,
            tx_hash: sample.data.tx_hash,
            edges: remaining,
        });
    }

    if (bundles.length === 0) return elements;

    // Identify nodes safe to remove: a co-spender node is removable iff EVERY
    // edge it touches is in some bundle. Nodes with side connections (entity
    // labels, KYC, other txs, etc.) must stay.
    const edgesPerNode = new Map<string, Set<string>>();  // node_id → set of edge ids
    for (const e of edges) {
        const sId = e.data.source;
        const tId = e.data.target;
        (edgesPerNode.get(sId) ?? edgesPerNode.set(sId, new Set()).get(sId)!).add(e.data.id);
        (edgesPerNode.get(tId) ?? edgesPerNode.set(tId, new Set()).get(tId)!).add(e.data.id);
    }

    const nodeIdsToRemove = new Set<string>();
    const bundleAbsorbedNodes: Map<number, NodeRow[]> = new Map();  // bundle_index → [removed node rows]

    bundles.forEach((b, bi) => {
        // For each bundled edge, the "co-spender" is the side OPPOSITE the convergence node.
        const cospenderIds = b.edges.map(e =>
            b.side === 'in' ? e.data.source : e.data.target
        );
        const absorbed: NodeRow[] = [];
        for (const cId of cospenderIds) {
            if (cId === b.convergence) continue;  // safety
            if (nodeIdsToRemove.has(cId)) continue;  // already counted
            const touchedEdges = edgesPerNode.get(cId);
            if (!touchedEdges) continue;
            // Removable iff every edge it touches is in SOME bundle (claimed)
            const allClaimed = Array.from(touchedEdges).every(eId => edgeIdsClaimed.has(eId));
            if (allClaimed) {
                nodeIdsToRemove.add(cId);
                const nodeRow = nodes.find(n => n.data.id === cId);
                if (nodeRow) absorbed.push(nodeRow);
            }
        }
        bundleAbsorbedNodes.set(bi, absorbed);
    });

    // Build the new elements array.
    const out: any[] = [];

    // Nodes: keep all that aren't being absorbed
    for (const n of nodes) {
        if (!nodeIdsToRemove.has(n.data.id)) out.push(n);
    }

    // Edges: keep all that aren't being bundled
    for (const e of edges) {
        if (!edgeIdsClaimed.has(e.data.id)) out.push(e);
    }

    // Cluster nodes + bundle edges
    bundles.forEach((b, bi) => {
        const clusterId = `cluster:${b.tx_hash.slice(0, 16)}:${b.side}:${b.convergence.slice(0, 12)}`;
        const absorbed = bundleAbsorbedNodes.get(bi) ?? [];
        const count = b.edges.length;
        const label =
            b.side === 'in'
                ? `${count} co-inputs · likely batch/mix`
                : `${count} co-outputs · likely batch/payout`;

        out.push({
            data: {
                id: clusterId,
                label,
                type: 'cluster',
                cluster_kind: b.side,
                tx_hash: b.tx_hash,
                cospender_count: count,
                bundled_edges: b.edges.map(e => e.data),
                bundled_node_ids: absorbed.map(n => n.data.id),
                bundled_nodes: absorbed.map(n => n.data),
                convergence_node_id: b.convergence,
            },
        });

        // The single bundle edge connects cluster ↔ convergence node.
        // Direction: "in" → cluster→convergence (sources flowed in),
        //            "out" → convergence→cluster (destinations flowed out).
        const bundleEdge = b.side === 'in'
            ? { source: clusterId, target: b.convergence }
            : { source: b.convergence, target: clusterId };

        out.push({
            data: {
                id: `${clusterId}:edge`,
                ...bundleEdge,
                tx_hash: b.tx_hash,
                is_bundle: true,
                cluster_kind: b.side,
                cospender_count: count,
                label,
            },
        });
    });

    return out;
}


/**
 * Inverse: expand a single cluster back to its original edges + nodes.
 * Returns an updated elements array.
 *
 * Idempotent — if the cluster id is missing, returns elements unchanged.
 *
 * If `centerPosition` is supplied, bundled nodes are seeded on a circle around
 * that point so they're visible immediately on the canvas instead of piling up
 * at (0,0) and waiting for a layout pass that never re-runs. Caller can then
 * fit the camera to the new ids returned in `expandedNodeIds`.
 */
export interface ExpandResult {
    elements: any[];
    /** Ids of the nodes that were re-added (caller can fit/animate to these). */
    expandedNodeIds: string[];
}

export function expandStarburstCluster(
    elements: any[],
    clusterNodeId: string,
    centerPosition?: { x: number; y: number },
): any[] {
    return expandStarburstClusterDetailed(elements, clusterNodeId, centerPosition).elements;
}

export function expandStarburstClusterDetailed(
    elements: any[],
    clusterNodeId: string,
    centerPosition?: { x: number; y: number },
): ExpandResult {
    const cluster = elements.find(el => el?.data?.id === clusterNodeId && el?.data?.type === 'cluster');
    if (!cluster) return { elements, expandedNodeIds: [] };
    const bundledEdges = cluster.data.bundled_edges || [];
    const bundledNodes = cluster.data.bundled_nodes || [];
    const bundleEdgeId = `${clusterNodeId}:edge`;
    const out: any[] = [];
    for (const el of elements) {
        // Drop the cluster node + its bundle edge
        if (el?.data?.id === clusterNodeId) continue;
        if (el?.data?.id === bundleEdgeId) continue;
        out.push(el);
    }
    // Re-add the original nodes (skip if somehow already present).
    // Seed positions on concentric rings around centerPosition so they're
    // visible immediately — without this they pile up at (0,0) and the user
    // can't tell the cluster expanded at all (only re-running the full
    // layout would otherwise place them, and we don't trigger that).
    //
    // Single ring crowds for large bundles: 228 nodes on a 260px ring = ~7px
    // arc per node, completely overlapping. We instead pack ~SPACING px of
    // arc length per node across as many rings as needed.
    const existingIds = new Set(out.map(el => el?.data?.id));
    const expandedNodeIds: string[] = [];
    const n = bundledNodes.length || 1;
    const ringPositions = computeRingLayout(n);
    bundledNodes.forEach((bn: any, i: number) => {
        if (existingIds.has(bn.id)) return;
        // R24: declare role. Ring positions are a user-action expand affordance
        // (click-to-expand cluster) — not algorithm discovery coordinates.
        const node: any = {
            data: {
                ...bn,
                placement_role: bn.placement_role || 'context',
            },
        };
        if (centerPosition) {
            const offset = ringPositions[i];
            node.position = {
                x: centerPosition.x + offset.x,
                y: centerPosition.y + offset.y,
            };
        }
        out.push(node);
        expandedNodeIds.push(bn.id);
    });
    for (const e of bundledEdges) {
        if (!existingIds.has(e.id)) out.push({ data: e });
    }
    return { elements: out, expandedNodeIds };
}


/**
 * Distribute n points across one or more concentric rings around (0,0) so
 * that each point gets at least MIN_ARC px of arc length on its ring. Inner
 * ring radius is BASE_RADIUS; each outer ring adds RING_GAP.
 *
 * For small n (≤24) this collapses to a single ring identical to the old
 * behaviour. For 228 it produces ~6 rings spanning ~80–680px instead of one
 * impossibly crowded 260px ring.
 */
function computeRingLayout(n: number): Array<{ x: number; y: number }> {
    const BASE_RADIUS = 90;     // first ring radius
    const RING_GAP = 90;        // distance between successive rings
    const MIN_ARC = 32;         // min arc length per node, px

    const out: Array<{ x: number; y: number }> = new Array(n);
    let placed = 0;
    let ring = 0;

    while (placed < n) {
        const radius = BASE_RADIUS + ring * RING_GAP;
        const circumference = 2 * Math.PI * radius;
        // How many fit on this ring at our minimum spacing
        const capacity = Math.max(8, Math.floor(circumference / MIN_ARC));
        const count = Math.min(capacity, n - placed);
        // Stagger ring rotation so adjacent rings don't align radially
        const phase = (ring * Math.PI) / Math.max(8, count);
        for (let i = 0; i < count; i++) {
            const angle = phase + (2 * Math.PI * i) / count;
            out[placed + i] = {
                x: radius * Math.cos(angle),
                y: radius * Math.sin(angle),
            };
        }
        placed += count;
        ring += 1;
    }
    return out;
}


/**
 * Re-collapse an expanded cluster back to its bundle. Useful when the user
 * clicks the convergence node a second time or hits a "collapse" affordance.
 *
 * Implementation note: this re-runs bundleStarburstEdges on the current
 * elements, which will rebuild the cluster naturally if the threshold is still
 * exceeded. Callers that want a single-cluster collapse should track the
 * cluster id list themselves; this helper is for "compact everything again".
 */
export function recollapseStarbursts(elements: any[], opts: BundleOptions = {}): any[] {
    return bundleStarburstEdges(elements, opts);
}
